import { Language, LanguageType } from '@nx-smartmonkey/shared/domain';
import moment from 'moment';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { BinTypes } from '../../../../domain/analytics/BinTypes';
import { useRetrieveActiveLanguage } from '../../../../hooks/translations/useRetrieveActiveLanguage';

export const getLabelByBinType = (date: string, binType: BinTypes, locale: LanguageType) => {
  if (binType === `day`) {
    const localeMoment = moment(date).locale(locale);
    const day = localeMoment.format(`ddd`);
    return (day.charAt(0).toUpperCase() + day.slice(1)).replace(/\./g, ``);
  } else {
    const localeMoment = moment(date).locale(locale);
    return localeMoment.format(`DD`);
  }
};

export const TABS = [`distance`, `stops`, `emissions`] as const;
export type TabTypes = (typeof TABS)[number];

export interface UseAnalyticsFiltersResponse {
  tab: TabTypes;
  binType: BinTypes;
  startDate: Date;
  endDate: Date;
  setTabParam: (newTabParam: TabTypes) => void;
  setBinType: (newBinType: BinTypes) => void;
  setStartDate: (newStartDate: Date) => void;
}

export const useAnalyticsFilters = (): UseAnalyticsFiltersResponse => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeLanguage } = useRetrieveActiveLanguage();
  const language = activeLanguage ?? Language.getDefaultLanguage().value;

  const binType = (searchParams.get(`bin_type`) ?? `day`) as BinTypes;

  const tabParam = (searchParams.get(`tab`) ?? ``) as TabTypes;
  const tab = TABS.includes(tabParam) ? tabParam : `distance`;

  const startDateParam = searchParams.get(`start_date`);
  const [startDate, endDate] = useMemo(() => {
    const tempStartDate = startDateParam ? new Date(startDateParam) : new Date();

    if (binType === `day`) {
      // Get the first day of the current week
      const startOfWeek = moment(tempStartDate).locale(language).startOf(`week`);
      const endOfWeek = moment(tempStartDate).locale(language).endOf(`week`);
      return [startOfWeek.toDate(), endOfWeek.toDate()];
    }

    // Get the first day of the current month
    const startOfMonth = moment(tempStartDate).locale(language).startOf(`month`);
    const endOfMonth = moment(tempStartDate).locale(language).endOf(`month`);
    return [startOfMonth.toDate(), endOfMonth.toDate()];
  }, [startDateParam, binType, language]);

  return {
    tab,
    binType,
    startDate,
    endDate,
    setTabParam: (newTabParam: TabTypes) => {
      searchParams.set(`tab`, newTabParam);
      setSearchParams(searchParams);
    },
    setBinType: (newBinType: BinTypes) => {
      searchParams.set(`bin_type`, newBinType);
      setSearchParams(searchParams);
    },
    setStartDate: (newStartDate: Date) => {
      searchParams.set(`start_date`, newStartDate.toUTCString());
      setSearchParams(searchParams);
    },
  };
};
